import { definePage } from '@reflex/plugin-api/web';
import type { PluginWebManifest } from '@reflex/plugin-api/web';

const page = definePage({
  path: '/town',
  label: 'Town',
  icon: 'Trees',
  group: 'misc',
  componentPath: '@reflex/plugin-town/web/TownPage',
  description: 'Live agent visualization, grouped by label, repo, or state.',
  details:
    'A pixel-art town view where each agent is a character. Group agents into labelled districts by label, git repo, or lifecycle state, filter the roster by status, repo, or label, and search by name or model. Click any character to see agent details, stop or archive it.',
  features: [
    'Live agent sprites',
    'Group by label, repo, or state',
    'Filter by status, repo, or label',
    'Click to inspect',
    'Stop & archive actions',
    'Real-time updates',
  ],
});

const flowTownPage = definePage({
  path: '/town/flows',
  label: 'Automation Town',
  icon: 'Workflow',
  group: 'misc',
  componentPath: '@reflex/plugin-town/web/FlowTownPage',
  description: 'Live automation run visualization.',
  details:
    'A pixel-art village view where every active automation run is its own outpost. Each node renders as a building, edges as roads, and agent nodes spawn pinned characters at their workshop. Click any building to inspect a node.',
  features: [
    'One outpost per active run',
    'Status-driven visuals',
    'Click to inspect nodes',
    'Real-time updates',
  ],
});

const farmPage = definePage({
  path: '/farm',
  label: 'Farm',
  icon: 'Sprout',
  group: 'misc',
  componentPath: '@reflex/plugin-town/web/FarmPage',
  description:
    'Till, plant, water, and sleep your way through the seasons in a cozy top-down farming sim.',
  details:
    'A standalone pixel-art farming game. Till plots inside the fence, plant by the season, water daily, and sleep in your farmhouse to grow your crops — then harvest quality produce to sell, cook, or automate with sprinklers. Tame wild critters into companions that follow you and forage, and round out the homestead with mining, fishing, and horses.',
  features: [
    'Seasons & day cycle',
    'Crop quality',
    'Sprinkler automation',
    'Animal taming & companions',
    'Farmhouse',
    'Mining',
    'Fishing',
    'Horses',
  ],
  fullscreen: true,
});

export const townWeb: PluginWebManifest = {
  ...page.web,
  routes: [
    ...(page.web.routes ?? []),
    ...(flowTownPage.web.routes ?? []),
    ...(farmPage.web.routes ?? []),
  ],
  navItems: [
    ...(page.web.navItems ?? []),
    ...(flowTownPage.web.navItems ?? []),
    ...(farmPage.web.navItems ?? []),
  ],
  commands: [
    ...(page.web.commands ?? []),
    ...(flowTownPage.web.commands ?? []),
    ...(farmPage.web.commands ?? []),
  ],
  overviewCards: [
    ...(page.web.overviewCards ?? []),
    ...(flowTownPage.web.overviewCards ?? []),
    ...(farmPage.web.overviewCards ?? []),
  ],
  agentActions: [
    {
      label: 'Town View',
      icon: 'Trees',
      to: '/town?selected={agentId}',
      category: 'environment',
    },
  ],
};
